import React from 'react'
import { Checkbox, Select, type SelectOption } from '../Common'

export type EduAudienceKind = 'ALL' | 'ROOT' | 'NODES'

export interface EduAudiencePickerNode {
  id: string
  title: string
}

export interface EduAudiencePickerLabels {
  audienceLabel: string
  rootLabel: string
}

export interface EduAudiencePickerProps {
  audienceKind: EduAudienceKind
  onChangeAudienceKind: (kind: EduAudienceKind) => void
  selectedRootId: string
  onChangeSelectedRootId: (rootId: string) => void
  selectedNodeIds: string[]
  onChangeSelectedNodeIds: (nodeIds: string[]) => void
  availableRoots: EduAudiencePickerNode[]
  availableClassNodes: EduAudiencePickerNode[]
  audienceOptions: SelectOption<EduAudienceKind>[]
  labels: EduAudiencePickerLabels
  disabled?: boolean
  sectionClassName?: string
  labelClassName?: string
  subSectionClassName?: string
  checkboxesClassName?: string
}

export const EduAudiencePicker: React.FC<EduAudiencePickerProps> = ({
  audienceKind,
  onChangeAudienceKind,
  selectedRootId,
  onChangeSelectedRootId,
  selectedNodeIds,
  onChangeSelectedNodeIds,
  availableRoots,
  availableClassNodes,
  audienceOptions,
  labels,
  disabled = false,
  sectionClassName,
  labelClassName,
  subSectionClassName,
  checkboxesClassName,
}) => {
  const toggleNodeSelection = (nodeId: string) => {
    if (selectedNodeIds.includes(nodeId)) {
      onChangeSelectedNodeIds(selectedNodeIds.filter((id) => id !== nodeId))
    } else {
      onChangeSelectedNodeIds([...selectedNodeIds, nodeId])
    }
  }

  return (
    <div className={sectionClassName || 'eduAudiencePicker'}>
      <span className={labelClassName || 'eduAudiencePicker__label'}>
        {labels.audienceLabel}
      </span>
      <Select<EduAudienceKind>
        value={audienceKind}
        onChange={onChangeAudienceKind}
        options={audienceOptions}
        disabled={disabled}
        ariaLabel={labels.audienceLabel}
      />

      {audienceKind === 'ROOT' && (
        <div className={subSectionClassName || 'eduAudiencePicker__subSection'}>
          <Select<string>
            value={selectedRootId}
            onChange={onChangeSelectedRootId}
            options={availableRoots.map((r) => ({
              value: r.id,
              label: r.title,
            }))}
            disabled={disabled}
            ariaLabel={labels.rootLabel}
          />
        </div>
      )}

      {audienceKind === 'NODES' && (
        <div className={checkboxesClassName || 'eduAudiencePicker__nodeCheckboxes'}>
          {availableClassNodes.map((n) => (
            <Checkbox
              key={n.id}
              label={n.title}
              checked={selectedNodeIds.includes(n.id)}
              onChange={() => toggleNodeSelection(n.id)}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </div>
  )
}
